import React, { useEffect, useState } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { Printer, ArrowRight, ArrowLeft, FileText } from "lucide-react";
import api from "@/lib/api";
import { formatKWD } from "@/lib/format";
import { useLang } from "@/context/LanguageContext";
import { StaffLangToggle } from "@/components/StaffLangToggle";

const STORE = {
  name: "RNF_Line | رنف لاين",
  phone: "+965 6638 2446",
  address: "الشويخ، مجمع A5، بوابة 3، محل رقم 321، الكويت",
};
const PAY_LABEL = { cash: "نقداً", knet: "كي نت", visa: "فيزا", mastercard: "ماستركارد" };
const M_LABELS = { length: "الطول", shoulder: "الكتف", chest: "الصدر", sleeve: "طول الكم", hip: "محيط الورك" };
const DELIV = { pickup: "استلام من المحل", local: "توصيل داخل الكويت", intl: "شحن دولي" };

export default function Receipt() {
  const { id } = useParams();
  const nav = useNavigate();
  const location = useLocation();
  const [inv, setInv] = useState(null);
  const [policy, setPolicy] = useState("");
  const [store, setStore] = useState({ phone: STORE.phone, address: STORE.address });
  const [size, setSize] = useState("80"); // 80 | a4
  const { lang, tr } = useLang();

  useEffect(() => {
    api.get(`/invoices/${id}`).then((r) => setInv(r.data)).catch(() => nav("/pos"));
    api.get("/settings").then((r) => {
      setPolicy(r.data.return_policy);
      setStore({ phone: r.data.phone || STORE.phone, address: r.data.address || STORE.address });
    }).catch(() => {});
  }, [id]);

  if (!inv) return <div className="py-24 text-center text-text-muted">…</div>;
  const dt = new Date(inv.created_at);
  const from = new URLSearchParams(location.search).get("from");
  const isReprint = new URLSearchParams(location.search).get("reprint") === "1" || from === "admin";
  const back = () => nav(from === "admin" ? "/admin/invoices" : from === "pos" ? "/pos" : (inv.source === "STORE" ? "/pos" : "/admin/invoices"));

  return (
    <div className="min-h-screen bg-[#F5F5F5] py-6">
      <div className="no-print max-w-[80mm] mx-auto mb-4 grid grid-cols-2 gap-2" style={{ maxWidth: "max(80mm, 320px)" }}>
        <button onClick={back} data-testid="receipt-back" className="flex items-center justify-center gap-2 bg-white border border-[#141110] min-h-[44px] text-sm font-medium">
          {lang === "en" ? <ArrowLeft className="w-4 h-4" /> : <ArrowRight className="w-4 h-4" />} {tr("رجوع", "Back")}
        </button>
        <button onClick={() => setSize(size === "80" ? "a4" : "80")} data-testid="receipt-toggle-size" className="flex items-center justify-center gap-2 bg-white border border-[#141110] min-h-[44px] text-sm font-medium">
          <FileText className="w-4 h-4" /> {size === "80" ? tr("معاينة A4", "A4 preview") : tr("معاينة 80mm", "80mm preview")}
        </button>
        <button onClick={() => window.print()} data-testid="receipt-print"
          className="col-span-2 flex items-center justify-center gap-3 bg-[#141110] text-white min-h-[60px] text-lg font-bold rounded-md shadow-md hover:bg-black active:scale-[0.99] transition-transform">
          <Printer className="w-6 h-6" /> {isReprint ? tr("إعادة طباعة الفاتورة", "Reprint Receipt") : tr("طباعة الفاتورة", "Print Receipt")} {size === "80" ? "(80mm)" : "(A4 / PDF)"}
        </button>
        <div className="col-span-2 flex items-center justify-between gap-2">
          <p className="text-[11px] text-text-muted" data-testid="receipt-reprint-note">{tr("الطباعة وإعادة الطباعة لا تُنشئ عملية بيع جديدة.", "Printing or reprinting never creates a new sale.")}</p>
          <StaffLangToggle dark={false} />
        </div>
      </div>

      <div className={size === "80" ? "receipt-80 shadow-sm" : "receipt-a4 shadow-sm"} data-testid="receipt-content" dir="rtl" lang="ar">
        <div style={{ textAlign: "center" }}>
          <img src="/logo.png" alt="RNF_Line" style={{ height: 56, width: "auto", margin: "0 auto 4px" }} />
          <div style={{ fontWeight: "bold", fontSize: 13 }}>{STORE.name}</div>
          <div><bdi dir="ltr" style={{ unicodeBidi: "isolate" }}>{store.phone}</bdi></div>
          <div style={{ fontSize: 10 }}>{store.address}</div>
        </div>
        <hr />
        <div>فاتورة رقم: #{inv.invoice_no}{inv.revision ? ` (نسخة ${inv.revision})` : ""}</div>
        <div>التاريخ: {dt.toLocaleString("ar-KW")}</div>
        {inv.seller_name && <div>البائعة: {inv.seller_name}</div>}
        <div>المصدر: {inv.source === "STORE" ? "المتجر" : "الموقع"}</div>
        {inv.customer_name && <div>العميلة: {inv.customer_name}{inv.customer_phone ? ` · ${inv.customer_phone}` : ""}</div>}
        {inv.delivery_type && <div>التسليم: {DELIV[inv.delivery_type] || inv.delivery_type}</div>}
        <hr />
        {inv.items.map((it, i) => (
          <div key={i} style={{ marginBottom: 6 }}>
            <div style={{ display: "flex", justifyContent: "space-between", fontWeight: "bold" }}>
              <span>{it.name_ar}{it.quantity > 1 ? ` ×${it.quantity}` : ""}</span>
              <span>{Number(it.line_total).toFixed(3)}</span>
            </div>
            <div style={{ fontSize: 9 }}>{it.sku || it.variant_sku}{it.size ? ` · ${it.size}/${it.color_ar || it.color}` : ""} · {it.sale_type === "ready" ? "جاهز" : "تفصيل حسب الطلب"}</div>
            {it.measurements && (
              <div style={{ fontSize: 9, color: "#444" }}>
                المقاسات: {Object.entries(it.measurements).map(([k, v]) => `${M_LABELS[k] || k} ${v}`).join(" · ")} سم
              </div>
            )}
            {it.notes && <div style={{ fontSize: 9, color: "#444" }}>ملاحظات: {it.notes}</div>}
          </div>
        ))}
        <hr />
        <div style={{ display: "flex", justifyContent: "space-between" }}><span>المجموع</span><span>{Number(inv.subtotal).toFixed(3)}</span></div>
        <div style={{ display: "flex", justifyContent: "space-between" }}><span>التوصيل / الشحن</span><span>{Number(inv.delivery_fee || 0).toFixed(3)}</span></div>
        <div style={{ display: "flex", justifyContent: "space-between", fontWeight: "bold", fontSize: 13 }}>
          <span>الإجمالي</span><span>{formatKWD(inv.total, "ar")}</span>
        </div>
        <div style={{ display: "flex", justifyContent: "space-between" }}>
          <span>طريقة الدفع</span><span>{PAY_LABEL[inv.payment_method] || inv.payment_method}</span>
        </div>
        <div style={{ display: "flex", justifyContent: "space-between" }}>
          <span>حالة الدفع</span><span>{inv.status === "cancelled" ? "ملغاة" : "مدفوعة"}</span>
        </div>
        <hr />
        <div style={{ fontSize: 10, textAlign: "center" }}>{policy}</div>
        <div style={{ textAlign: "center", marginTop: 6 }}>@rnf_line — شكراً لتسوقك</div>
      </div>
    </div>
  );
}
